import React, {useState, useRef, useCallback} from "react"
import styled, {css} from "styled-components"
import {Input} from "@irati/ui/src/Typography"
import {StyledRouterLink} from "./shared/StyledLink"
import Flex from "@irati/ui/src/Flex"
import {Department} from "../utils/departments"
import Loading, {LoadType} from "@irati/ui/src/Loading"

interface PillsBarProps {
  departments: Department[]
  load?: LoadType
}

export const PillsBar = ({departments, load}: PillsBarProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollRef.current) return
    setIsDragging(true)
    setStartX(e.pageX - scrollRef.current.offsetLeft)
    setScrollLeft(scrollRef.current.scrollLeft)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !scrollRef.current) return
      e.preventDefault()
      const x = e.pageX - scrollRef.current.offsetLeft
      const walk = (x - startX) * 2
      scrollRef.current.scrollLeft = scrollLeft - walk
    },
    [isDragging, startX, scrollLeft]
  )

  return (
    <Loading load={load}>
      <ScrollContainer
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        $isDragging={isDragging}>
        <Flex inline gap={1.5} paddingTop={3} paddingBottom={3}>
          {departments &&
            departments.map((department) => (
              <StyledRouterLink to={`/departments/${department.id}`} key={department.name}>
                <Pill key={department.name}>
                  <Input>{department.name}</Input>
                </Pill>
              </StyledRouterLink>
            ))}
        </Flex>
      </ScrollContainer>
    </Loading>
  )
}

const ScrollContainer = styled.div<{$isDragging: boolean}>`
  overflow-x: scroll;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  white-space: nowrap;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  cursor: ${({$isDragging}) => ($isDragging ? "grabbing" : "grab")};
  user-select: none;

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`

const Pill = styled.div`
  ${({theme}) => css`
    border-radius: 100px;
    background-color: ${theme.colors.greys.white};
    border: 0.5px solid #d2d2d7;
    box-shadow: ${theme.shadows.mid};
    height: 45px;
    padding: ${theme.spacing.one} ${theme.spacing.two};
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
  `}
`
